import { useRef, useState } from 'react';
import { MAX_BUCKET_NAME_LENGTH, MAX_DOCUMENT_LABEL_LENGTH } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { BucketRows } from '../components/BucketRows';
import { RemoteListStates } from '../components/RemoteListStates';
import { PencilIcon, PlusIcon, SearchIcon, CopyIcon, WorkspaceIcon } from '../components/Icons';
import { InlineError } from '../components/SidebarState';
import { useActivateBucket, useBucketList } from '../sidebar/data';
import { useSidebarHistory } from '../sidebar/history';
import { SUPER_COPY_PATH, WORKSPACES_PATH, bucketPath } from '../sidebar/routes';
import { createOperationId, truncateChars } from '../sidebar/presentation';
import { displayError, type DisplayError } from '../bridge/errors';

// A bucket for one line of business comes from Cara, which builds one when it
// fills a workspace's ACORD forms; here the agent makes general buckets only.
type CreateSource = 'copy' | 'workspace' | 'blank';

export function BucketsView() {
  const [search, setSearch] = useState('');
  const buckets = useBucketList(search);

  return (
    <section className="buckets-view" aria-labelledby="buckets-heading">
      <BucketCreator />

      <label className="search-field search-field--buckets">
        <SearchIcon />
        <span className="sr-only">Search data buckets</span>
        <input
          type="search"
          placeholder="Search data buckets"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>

      <div className="section-heading">
        <h2 id="buckets-heading">Your data buckets</h2>
      </div>

      <RemoteListStates
        list={buckets}
        searching={Boolean(search.trim())}
        loading={{ title: 'Loading buckets', description: 'Finding your recent Smart Fill data.' }}
        errorTitle="Buckets could not load"
        empty={{
          searchTitle: 'No matching data buckets',
          searchDescription: 'Try a different client or bucket name.',
          initialTitle: 'No data buckets yet',
          initialDescription:
            'Create a data bucket from this page, a workspace, or a blank set of fields.'
        }}
      />

      <BucketRows items={buckets.items} onChanged={buckets.reload} />
    </section>
  );
}

function BucketCreator() {
  const { client, snapshotRevision, applySuperCopy } = useSidebarSession();
  const history = useSidebarHistory();
  const activate = useActivateBucket();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [source, setSource] = useState<CreateSource>('copy');
  const [operationId, setOperationId] = useState(createOperationId);
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const [error, setError] = useState<DisplayError | null>(null);
  const needsName = source === 'blank';

  const reset = () => {
    setCreating(false);
    setName('');
    setSource('copy');
    setOperationId(createOperationId());
    setError(null);
  };

  const create = async () => {
    if (saving.current) return;
    if (source === 'workspace') {
      history.go(WORKSPACES_PATH);
      return;
    }
    if (needsName && !name.trim()) {
      setError({
        code: 'INVALID_REQUEST',
        message: 'Enter a bucket name.',
        retryable: false
      });
      return;
    }
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      if (source === 'copy') {
        const sinceRevision = snapshotRevision('superCopy');
        applySuperCopy(await client.request('superCopy.start', {}), { sinceRevision });
        history.go(SUPER_COPY_PATH);
        return;
      }
      const bucket = await client.request('buckets.create', {
        operationId,
        name: name.trim(),
        visibility: 'PRIVATE',
        // An empty client entity; fields are added in the bucket detail view.
        document: {
          version: 1,
          root: {
            type: 'client',
            label: truncateChars(name.trim(), MAX_DOCUMENT_LABEL_LENGTH)
          }
        }
      });
      await activate.activate(bucket.id);
      history.go(bucketPath(bucket.id));
      setOperationId(createOperationId());
    } catch (requestError) {
      setError(displayError(requestError, 'Could not create this bucket.'));
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="new-bucket-button"
        aria-expanded={creating}
        onClick={() => {
          setCreating((value) => !value);
          setError(null);
        }}
      >
        <PlusIcon />
        New data bucket
      </button>

      {creating && (
        <form
          className="create-panel"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          {needsName && (
            <>
              <label className="sr-only" htmlFor="new-bucket-name">
                Bucket name
              </label>
              <input
                autoFocus
                id="new-bucket-name"
                className="create-panel__name"
                placeholder="Bucket name"
                value={name}
                onChange={(event) =>
                  setName(truncateChars(event.target.value, MAX_BUCKET_NAME_LENGTH))
                }
              />
            </>
          )}

          <div className="source-options" role="group" aria-label="Bucket source">
            <SourceButton
              active={source === 'copy'}
              label="Page"
              icon={<CopyIcon />}
              onClick={() => setSource('copy')}
            />
            <SourceButton
              active={source === 'workspace'}
              label="Workspace"
              icon={<WorkspaceIcon />}
              onClick={() => setSource('workspace')}
            />
            <SourceButton
              active={source === 'blank'}
              label="Blank"
              icon={<PencilIcon />}
              onClick={() => setSource('blank')}
            />
          </div>
          <p className="create-panel__hint">{sourceHint(source)}</p>
          {error && <InlineError message={error.message} />}
          {activate.error && <InlineError message={activate.error.message} />}
          <div className="create-panel__actions">
            <button
              type="button"
              className="button button--secondary button--pill"
              disabled={busy}
              onClick={reset}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary button--pill"
              disabled={busy}
              aria-busy={busy}
            >
              {submitLabel(source)}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

function submitLabel(source: CreateSource): string {
  if (source === 'workspace') return 'Choose workspace';
  return source === 'copy' ? 'Create bucket from page' : 'Create data bucket';
}

function SourceButton({
  active,
  label,
  icon,
  onClick
}: {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={active ? 'source-button is-active' : 'source-button'}
      aria-pressed={active}
      onClick={onClick}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function sourceHint(source: CreateSource): string {
  switch (source) {
    case 'copy':
      return 'Create a bucket from this page. Review the captured facts before saving.';
    case 'workspace':
      return 'Pulls the fields from a Cara workspace run.';
    case 'blank':
      return 'Starts empty — you add fields yourself.';
  }
}
